import { zodResolver } from '@hookform/resolvers/zod'
import { Box } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useLoggedInUser } from '../../app/hooks/useLoggedInUser'
import { useNotificationStack } from '../../app/hooks/useNotificationStack'
import { useCreateBrandMutation, useUpdateBrandMutation } from '../../app/redux-fetch/apiAssets'
import SubmitButtons from '../../components/Buttons/SubmitButtons'
import FormInput from '../../components/Forms/FormInput'
import SideModalWrapper from '../Assets/modals/SideModalWrapper'
import { messages } from './messages'
import { BrandFormData, getBrandSchema } from './schema'
import { Brand } from './types'

type BrandFormProps = {
    brand: Brand
    handleClose: () => void
    isOpen: boolean
}

const MakeForm = ({ brand, handleClose, isOpen }: BrandFormProps) => {
    const { formatMessage } = useIntl()
    const { tenantId } = useLoggedInUser()
    const isNew = !brand?.id || brand?.id === 0

    const [createBrand] = useCreateBrandMutation()
    const [updateBrand] = useUpdateBrandMutation()
    const { enqueueSuccess, enqueueFailure } = useNotificationStack()

    const methods = useForm<BrandFormData>({
        resolver: zodResolver(getBrandSchema(formatMessage)),
        defaultValues: brand,
    })

    const {
        handleSubmit,
    } = methods

    const onSubmit = async (data: BrandFormData) => {
        if (isNew) {
            await createBrand({ tenantId, body: { ...data, tenantId } })
                .then(() => {
                    enqueueSuccess(messages.makeCreated)
                    handleClose()
                })
                .catch(() => enqueueFailure(messages.makeNotAdded))
        } else {
            await updateBrand({ tenantId, body: { ...brand, ...data } })
                .then(() => {
                    enqueueSuccess(messages.makeUpdated)
                    handleClose()
                })
                .catch(() => enqueueFailure(messages.makeNotUpdated))
        }
    }

    const onSubmitHandler = handleSubmit(onSubmit)

    return (
        <SideModalWrapper
            open={isOpen}
            onClose={handleClose}
            headerLabel={formatMessage(messages.createEditMake)}
        >
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Box sx={{ minWidth: '450px', m: '32px' }}>
                        <FormInput
                            name="name"
                            label={formatMessage(messages.name)}
                            required
                            maxLength={250}
                        />
                    </Box>
                    <Box
                        sx={{
                            position: 'fixed',
                            bottom: 0,
                            width: '100%',
                            padding: '16px',
                            backgroundColor: '#fff',
                            borderTop: '1px solid #e0e0e0',
                            zIndex: 999,
                        }}
                    >
                        <SubmitButtons
                            create={isNew}
                            isEnabled={true}
                            onCancel={handleClose}
                            submitForm={onSubmitHandler}
                        />
                    </Box>
                </form>
            </FormProvider>
        </SideModalWrapper>
    )
}

export default MakeForm
